import type { Location } from "@sitescene/types";

import { apiClient, type ApiResponse } from "../client";
import { endpoints } from "../endpoints";

/**
 * Locations API Service
 *
 * Pure functions for location-related API calls.
 * No React dependencies - can be used anywhere.
 *
 * This is the API layer - it only handles HTTP communication.
 * Business logic and state management happen in React Query hooks.
 */

export const locationsApi = {
	/**
	 * Create a new location
	 */
	createLocation: async (
		location: Omit<
			Location,
			"createdAt" | "displayMode" | "id" | "status" | "updatedAt" | "zones"
		>,
	): Promise<Location> => {
		const response = await apiClient.post<Location>(
			endpoints.locations.create,
			location,
		);
		return response.data;
	},

	/**
	 * Delete a location
	 */
	deleteLocation: async (locationId: string): Promise<void> => {
		await apiClient.delete<void>(endpoints.locations.delete(locationId));
	},

	/**
	 * Get a single location by ID
	 */
	getLocation: async (
		locationId: string,
		options?: {
			includeZonesAndSensors?: boolean;
		},
	): Promise<Location> => {
		const params = new URLSearchParams();
		if (options?.includeZonesAndSensors) {
			params.append("includeZonesAndSensors", "true");
		}

		const endpoint = `${endpoints.locations.getById(locationId)}${params.toString() ? `?${params.toString()}` : ""}`;
		const response = await apiClient.get<Location>(endpoint);
		return response.data;
	},

	/**
	 * Get all locations
	 */
	getLocations: async (options?: {
		includeZonesAndSensors?: boolean;
	}): Promise<Location[]> => {
		const params = new URLSearchParams();
		if (options?.includeZonesAndSensors) {
			params.append("includeZonesAndSensors", "true");
		}

		const endpoint = `${endpoints.locations.get}${params.toString() ? `?${params.toString()}` : ""}`;
		const response = await apiClient.get<Location[]>(endpoint);
		return response.data;
	},

	/**
	 * Get paginated locations with cursor-based pagination
	 * @param limit Maximum number of results per page (default: 100)
	 * @param after Cursor for pagination (optional)
	 * @returns Promise with paginated response including data and pagination metadata
	 */
	getLocationsPaginated: async (
		limit: number = 100,
		after?: string,
	): Promise<ApiResponse<Location[]>> => {
		const params = new URLSearchParams();
		if (limit !== undefined) {
			params.append("limit", limit.toString());
		}
		if (after) {
			params.append("after", after);
		}

		const endpoint = `${endpoints.locations.get}${params.toString() ? `?${params.toString()}` : ""}`;
		const response = await apiClient.get<Location[]>(endpoint);
		return response;
	},

	/**
	 * Update an existing location
	 */
	updateLocation: async (
		locationId: string,
		updates: Partial<Location>,
	): Promise<Location> => {
		const response = await apiClient.patch<Location>(
			endpoints.locations.update(locationId),
			updates,
		);
		return response.data;
	},
};
